/* eslint-disable react-hooks/exhaustive-deps */
import { IXGridBulkActionButton } from '@AMIEWEB/Common/XGrid/Toolbar/XGridToolbar';
import { XGrid } from '@AMIEWEB/Common/XGrid/XGrid';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { GridSelectionModel, GridSortModel } from '@mui/x-data-grid-pro';
import { Grid } from '@mui/material';
import { globalActions } from 'app/ApplicationRoot/Global.redux';
import { IAdults, gridTags } from 'app/models/Placement/PlacementMods';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePromiseTracker } from 'react-promise-tracker';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectCurrentModDetails,
  selectEditPreferenceAdultRows,
  selectExpandedModsDialog,
  selectIsEditExistingMod,
  selectIsEditMod,
  selectIsUndoModal,
  selectPreferenceAdultNameError,
  selectPreferenceAdultRelationshipError,
} from 'store/redux-store/placement-mods/selectors';
import { placementModsAction } from 'store/redux-store/placement-mods/slice';
import { makeStyles } from 'tss-react/mui';
import { generateRandomId, getAdultsGridColumns } from './utils';

const expandedDetailPanelStyles = makeStyles()({
  expandedGrid: {
    fontSize: '14px',
    '& .MuiDataGrid-main': {
      minHeight: '60px',
    },
  },
});

export const AdultsInfoGrid = ({ adultsTableInfo }: { adultsTableInfo: IAdults[] }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  //*START - SELECTORS
  const isExpanded = useSelector(selectExpandedModsDialog);
  const isModEdit = useSelector(selectIsEditMod);
  const selectedModsDetails = useSelector(selectCurrentModDetails);
  const isUndoModal = useSelector(selectIsUndoModal);
  const isEditExistingMod = useSelector(selectIsEditExistingMod);

  //Grid Specific slectors
  const editPreferenceAdultRequests = useSelector(selectEditPreferenceAdultRows);
  const preferenceAdultNameError = useSelector(selectPreferenceAdultNameError);
  const preferenceAdultRelationshipError = useSelector(selectPreferenceAdultRelationshipError);
  //*END - SELECTORS
  const { classes } = expandedDetailPanelStyles();
  const [headerCount, setHeaderCount] = useState<number>(0);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [columns, setColumns] = useState<any>(
    getAdultsGridColumns(t, isExpanded, isModEdit, classes, editPreferenceAdultRequests),
  );
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [isDeleteDisabled, setIsDeleteDisabled] = useState(true);
  const [selectedIds, setSelectedIds] = useState<GridSelectionModel>([]);

  const { promiseInProgress: isCallingAPI } = usePromiseTracker({ area: 'preferences-details-api', delay: 0 });

  //SET COLUMNS
  useEffect(() => {
    const initialColumns = getAdultsGridColumns(t, isExpanded, isModEdit, classes, editPreferenceAdultRequests);
    setColumns(initialColumns);
  }, [t, isExpanded, isModEdit, editPreferenceAdultRequests]);

  //HANDLES THE ROW SELECTION,ENABLE/DISABLE DELETE BUTTON
  const onRowsSelectionHandler = (ids: GridSelectionModel) => {
    setSelectedIds(ids);
    const modData = isModEdit ? editPreferenceAdultRequests : adultsTableInfo;
    const selectedRowsData = ids.map(id => modData.find(row => row.id === id));
    selectedRowsData.length > 0 ? setIsDeleteDisabled(false) : setIsDeleteDisabled(true);
  };

  //DELETE SELECTED ROWS,UPDATES EDIT DATA STATE , ERRORS , MOD ALTERED STATUS
  const deleteHandler = () => {
    const modData = isModEdit ? editPreferenceAdultRequests : adultsTableInfo;
    const updatedDataAfterDelete = modData.filter(item => !selectedIds.includes(item.id));
    const updateNameError = preferenceAdultNameError.filter(item => !selectedIds.includes(item.id));
    const updateRelationshipError = preferenceAdultRelationshipError.filter(item => !selectedIds.includes(item.id));

    dispatch(placementModsAction.setAdultGridmodAltered(true));
    dispatch(placementModsAction.setPreferenceAdultNameError(updateNameError));
    dispatch(placementModsAction.setPreferenceAdultRelationshipError(updateRelationshipError));
    dispatch(placementModsAction.setEditAdultRows(updatedDataAfterDelete));

    setSelectedIds([]);
  };

  //ADD A NEW ROW,UPDATES EDIT DATA STATE , ERRORS , MOD ALTERED STATUS
  const addNewAdultRow = e => {
    const existingRow = editPreferenceAdultRequests?.slice(0);
    const adultNameError = preferenceAdultNameError?.slice(0);
    const adultRelationshipError = preferenceAdultRelationshipError?.slice(0);

    const newRow = {
      id: generateRandomId(),
      name: '',
      relationship: '',
    };
    existingRow.unshift(newRow);

    const nameError = {
      name: true,
      id: newRow?.id,
    };
    const relationshipError = {
      relationship: true,
      id: newRow?.id,
    };

    adultNameError.unshift(nameError);
    adultRelationshipError.unshift(relationshipError);
    dispatch(placementModsAction.setPreferenceAdultNameError(adultNameError));
    dispatch(placementModsAction.setPreferenceAdultRelationshipError(adultRelationshipError));
    dispatch(placementModsAction.setAdultGridmodAltered(true));
    dispatch(placementModsAction.setEditAdultRows(existingRow));
  };

  //REFRESH GRID DATA
  const handleRefreshGrid = () => {
    dispatch(placementModsAction.setAdultGridmodError(false));
    dispatch(
      placementModsAction.getPreferencesModDetailAction({
        placementId: selectedModsDetails.placementId,
        travelerOptionId: selectedModsDetails.travelerOptionId,
        newMod: !isEditExistingMod,
      }),
    );
  };

  //RESET COLUMN
  const resetColumns = () => {
    setColumns(getAdultsGridColumns(t, isExpanded, isModEdit, classes, editPreferenceAdultRequests));
  };

  //ADD/DELETE ACTION
  const rightActions = React.useMemo<IXGridBulkActionButton[]>(() => {
    return [
      {
        key: 'add-new-adultInfo',
        Icon: AddIcon,
        disabled: !isModEdit,
        onClick: e => {
          addNewAdultRow(e);
        },
        tooltipProps: {
          tooltipContent: 'Add Adult',
        },
      },
      {
        key: 'delete',
        Icon: DeleteOutlinedIcon,
        disabled: !selectedIds || selectedIds?.length === 0,
        onClick: () => deleteHandler(),
        tooltipProps: {
          tooltipContent: 'Delete',
        },
      },
    ];
  }, [
    isExpanded,
    isModEdit,
    isDeleteDisabled,
    selectedIds,
    editPreferenceAdultRequests,
    preferenceAdultNameError,
    preferenceAdultRelationshipError,
  ]);

  //GET HEADER COUNT METHOD
  const getHeaderCount = row => {
    const totalRowCount = row?.length;
    const nullRowCount = row?.filter(
      each => each?.name === '' || each?.name === null || each?.relationship === '' || each?.relationship === null,
    )?.length;
    setHeaderCount(totalRowCount - nullRowCount);
  };

  //SET GRID ERROR FOR NO DATA,UPDATE HEADER COUNT
  useEffect(() => {
    if (isModEdit) {
      setSortModel([]);
    }
    getHeaderCount(isModEdit ? editPreferenceAdultRequests : adultsTableInfo);
  }, [editPreferenceAdultRequests]);

  //UNDO ACTION
  useEffect(() => {
    if (isUndoModal) {
      dispatch(placementModsAction.setPreferenceAdultNameError([]));
      dispatch(placementModsAction.setPreferenceAdultRelationshipError([]));
      dispatch(placementModsAction.setAdultGridmodError(false));
      dispatch(placementModsAction.setIsUndoModal(false));
    }
  }, [isUndoModal]);

  //SET  CELL ERRORS
  useEffect(() => {
    if (
      preferenceAdultNameError?.some(val => val.name) ||
      preferenceAdultRelationshipError.some(val => val.relationship)
    ) {
      dispatch(placementModsAction.setAdultGridmodError(true));
    } else {
      dispatch(placementModsAction.setAdultGridmodError(false));
    }
  }, [preferenceAdultNameError, preferenceAdultRelationshipError, editPreferenceAdultRequests]);

  const sortChangeHandler = model => {
    setSortModel(model);
  };

  const AdultsInfoGrid = React.useMemo(() => {
    return (
      <Grid>
        <XGrid
          components={{
            NoRowsOverlay: () => {
              return (
                <div className="center" style={{ height: '100%' }}>
                  {t('placement.placementMods.noRecordsFound')}
                </div>
              );
            },
          }}
          gridTag={gridTags.ADULTGRIDTAG}
          rows={isModEdit ? editPreferenceAdultRequests || [] : adultsTableInfo || []}
          columns={columns}
          disableSelectionOnClick
          pagination={false}
          autoHeight={true}
          sx={{ minWidth: '500px' }}
          sortModel={sortModel}
          onSortModelChange={sortChangeHandler}
          hideFooter
          editMode="row"
          inDialog
          title={t('placement.placementMods.adults')}
          displayCustomCount={selectedIds?.length > 0 ? selectedIds?.length : headerCount}
          loading={isCallingAPI}
          className={classes.expandedGrid}
          checkboxSelection={isModEdit}
          selectionModel={selectedIds}
          onSelectionModelChange={ids => onRowsSelectionHandler(ids)}
          toolbarProps={{
            refreshButton: true,
            rightActions: rightActions,
            columnSelector: isModEdit ? false : true,
            columnSelectorProps: {
              fallbackPlacements: [
                'bottom-end',
                'bottom',
                'bottom-start',
                'top-start',
                'top',
                'top-end',
                'left',
                'right',
              ],
            },
            resetColumns: () => {
              resetColumns();
              dispatch(
                globalActions.setSnackBar({
                  message: 'Columns have been reset.',
                  severity: 'success',
                }),
              );
            },
            refreshGrid: () => {
              handleRefreshGrid();
            },
          }}
        />
      </Grid>
    );
  }, [adultsTableInfo, isExpanded, isCallingAPI, resetColumns, columns, sortModel, editPreferenceAdultRequests]);

  return <>{AdultsInfoGrid}</>;
};
